import { useCallback, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

/** Homepage popup: Registration Week 2027 + financial aid (marketing's graphic, Slack
 *  #website-pop-up, Oct 27). Built as real text, not the image, so it stays crisp and readable
 *  by screen readers. Shows once per browser session, homepage only (rendered by Home.tsx),
 *  and retires itself after registration week ends: no deploy needed. */
const RETIRE_AFTER = Date.parse("2026-12-04T23:59:59-07:00");
const DISMISS_KEY = "finaid-popup-2027";
const FIN_AID_URL = "https://hxp.org/hxp-financial-aid-program/";

const sans = { fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" };

const Rule = () => <hr className="mx-auto my-5 w-4/5 border-0 border-t border-foreground/80 md:my-6" />;

const FinAidPopup = () => {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const lastFocus = useRef<Element | null>(null);

  useEffect(() => {
    if (Date.now() > RETIRE_AFTER) return;
    try {
      if (sessionStorage.getItem(DISMISS_KEY)) return;
    } catch {
      /* storage blocked: still show it once for this page view */
    }
    const t = setTimeout(() => {
      lastFocus.current = document.activeElement;
      setOpen(true);
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  const dismiss = useCallback(() => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setOpen(false);
    (lastFocus.current as HTMLElement | null)?.focus?.();
  }, []);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        dismiss();
        return;
      }
      // Keep Tab focus inside the dialog.
      if (e.key !== "Tab" || !dialogRef.current) return;
      const els = dialogRef.current.querySelectorAll<HTMLElement>("a[href], button");
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dismiss]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300"
      onClick={dismiss}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="finaid-popup-title"
        className="relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-md bg-white px-6 pb-8 pt-12 text-center shadow-2xl motion-safe:animate-in motion-safe:zoom-in-95 motion-safe:duration-300 md:max-w-lg md:px-10 md:pb-10 md:pt-14"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeRef}
          type="button"
          onClick={dismiss}
          aria-label="Close"
          className="absolute right-3 top-3 rounded-full border border-hxp-sand-dark bg-white p-1.5 text-foreground/70 transition-colors hover:text-foreground"
        >
          <X size={18} />
        </button>

        <h2
          id="finaid-popup-title"
          className="text-[3.25rem] font-bold leading-[0.92] text-primary md:text-[4.25rem]"
          style={{ fontFamily: "'Oswald', system-ui, sans-serif", letterSpacing: "-0.01em" }}
        >
          Registration
          <br />
          Week 2027
        </h2>

        <Rule />
        <div style={sans} className="uppercase text-foreground">
          <p className="text-3xl tracking-[0.06em] md:text-4xl">Nov 30 – Dec 4</p>
          <p className="mt-2 text-base tracking-[0.06em] md:text-lg">Trips go live: November 4</p>
        </div>
        <Rule />
        <p style={sans} className="text-base uppercase tracking-[0.06em] text-foreground md:text-lg">
          Financial aid applications open now!
        </p>
        <Rule />

        <a
          href={FIN_AID_URL}
          target="_self"
          style={sans}
          className="inline-flex w-4/5 items-center justify-center rounded-lg bg-hxp-red-dark px-6 py-3.5 text-lg text-white transition-colors hover:bg-[#9d382e] md:text-xl"
        >
          More Information Here!
        </a>
      </div>
    </div>
  );
};

export default FinAidPopup;
